import { useEffect, useState } from "react";
import { Trash2, UserX } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, rupiah } from "../lib/api";
import PinConfirm from "./PinConfirm";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { FibiLoading } from "./FibiLoading";

const PERIODS = [{ v: 7, l: "1 minggu" }, { v: 30, l: "1 bulan" }, { v: 90, l: "3 bulan" }, { v: 180, l: "6 bulan" }];

export function InactiveAgentsDialog({ open, onClose, onDeleted }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState([]);
  const [pinOpen, setPinOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = (d) => { setData(null); setSel([]); api.get("/agents/inactive", { params: { days: d } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); };
  useEffect(() => { if (open) load(days); }, [open, days]);

  const rows = data?.rows || [];
  const deletable = rows.filter((r) => r.balance <= 0 && r.downlines === 0);
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const doDelete = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/agents/inactive/delete", { ids: sel });
      toast.success(`${res.deleted} agen dihapus${res.skipped?.length ? ` • dilewati: ${res.skipped.join(", ")}` : ""}`, { duration: 8000 });
      load(days); onDeleted?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[88vh] flex flex-col" data-testid="inactive-agents-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserX className="w-5 h-5 text-rose-500" /> Agen Tidak Aktif</DialogTitle>
          <DialogDescription>Agen tanpa aktivitas (cetak voucher, mutasi saldo, PPP, login) selama periode terpilih. Agen bersaldo atau yang punya downline tidak bisa dihapus massal.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Tidak aktif selama:</span>
          {PERIODS.map((p) => (
            <Button key={p.v} size="sm" variant={days === p.v ? "default" : "outline"} className={`h-7 text-xs ${days === p.v ? "bg-[#F04E23] hover:bg-[#d84420]" : ""}`} onClick={() => setDays(p.v)} data-testid={`inactive-period-${p.v}`}>{p.l}</Button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!deletable.length} onClick={() => setSel(sel.length === deletable.length ? [] : deletable.map((r) => r.id))} data-testid="inactive-select-all">{sel.length === deletable.length && deletable.length ? "Batal pilih" : `Pilih semua (${deletable.length})`}</Button>
            <Button size="sm" className="h-7 text-xs bg-rose-500 hover:bg-rose-600 text-white" disabled={!sel.length || busy} onClick={() => setPinOpen(true)} data-testid="inactive-delete-btn"><Trash2 className="w-3.5 h-3.5 mr-1" /> Hapus {sel.length} Agen</Button>
          </div>
        </div>
        <div className="overflow-auto flex-1 rounded-lg border border-border">
          {!data ? <div><FibiLoading text="FIBI sedang memuat..." size={20} /></div> : !rows.length ? <p className="text-sm text-emerald-400 text-center py-8" data-testid="inactive-empty">Semua agen aktif dalam periode ini.</p> : (
            <Table className="text-xs">
              <TableHeader><TableRow><TableHead className="w-8" /><TableHead>Agen</TableHead><TableHead>Tipe</TableHead><TableHead>Dibuat</TableHead><TableHead>Aktivitas Terakhir</TableHead><TableHead className="text-right">Saldo</TableHead><TableHead className="text-right">Downline</TableHead></TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const blocked = r.balance > 0 || r.downlines > 0;
                  return (
                    <TableRow key={r.id} className={sel.includes(r.id) ? "bg-rose-500/5" : ""} data-testid={`inactive-row-${r.code}`}>
                      <TableCell><input type="checkbox" className="h-4 w-4 accent-rose-500" disabled={blocked} checked={sel.includes(r.id)} onChange={() => toggle(r.id)} title={blocked ? "Bersaldo / punya downline — tidak bisa dihapus massal" : ""} data-testid={`inactive-check-${r.code}`} /></TableCell>
                      <TableCell><div className="font-medium"><span className="font-mono text-emerald-500 mr-1">{r.code}</span>{r.name}</div><div className="font-mono text-muted-foreground">{r.email || "-"}{!r.has_user && r.email && <span className="ml-1 text-amber-400">(tanpa akun login)</span>}</div></TableCell>
                      <TableCell>{r.type || "-"}</TableCell>
                      <TableCell className="font-mono">{r.created_at ? fmtDate(r.created_at) : "-"}</TableCell>
                      <TableCell className="font-mono">{r.last_activity ? fmtDate(r.last_activity) : <span className="text-muted-foreground">belum pernah</span>}</TableCell>
                      <TableCell className={`text-right font-mono ${r.balance > 0 ? "text-amber-400 font-bold" : ""}`}>{rupiah(r.balance)}</TableCell>
                      <TableCell className={`text-right font-mono ${r.downlines > 0 ? "text-amber-400 font-bold" : ""}`}>{r.downlines}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
        {data && <p className="text-[11px] text-muted-foreground">{rows.length} agen tidak aktif • {deletable.length} bisa dihapus</p>}
        <PinConfirm open={pinOpen} onOpenChange={setPinOpen} title={`Hapus ${sel.length} Agen Tidak Aktif?`} description="Akun agen beserta login-nya dihapus permanen. Riwayat transaksi lama tetap tersimpan." confirmLabel="Ya, Hapus" destructive onConfirm={doDelete} testid="pin-inactive-delete" />
      </DialogContent>
    </Dialog>
  );
}
